import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { getDefaultRoute } from "@/lib/rbac";
import XLSyncLogo from "@/components/brand/XLSyncLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_PASSWORD_LENGTH = 8;

export default function ResetPassword() {
  const { isAuthenticated, isLoading, permissions, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState("");
  const [tokenError, setTokenError] = useState("");

  if (!token) {
    return <Navigate to="/forgot-password" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--xl-bg)]">
        <div className="w-8 h-8 rounded-full border-2 border-[var(--xl-primary)] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (isAuthenticated) {
    return <Navigate to={getDefaultRoute(permissions, user?.role)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError("");
    setTokenError("");

    if (!password || password.length < MIN_PASSWORD_LENGTH) {
      setFieldError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (password !== confirmPassword) {
      setFieldError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      navigate("/login", {
        replace: true,
        state: { toastMessage: "Password updated, please log in." },
      });
    } catch (err) {
      const status = err?.response?.status;
      const detail =
        formatApiErrorDetail(err?.response?.data?.detail) ||
        err?.response?.data?.message ||
        "";

      if (status === 400) {
        setTokenError(
          "This reset link is invalid or expired. Please request a new one."
        );
      } else if (status === 422) {
        setFieldError(
          detail && detail !== "Something went wrong. Please try again."
            ? detail
            : `Password must be at least ${MIN_PASSWORD_LENGTH} characters`
        );
      } else {
        setFieldError(detail || "Could not reset password. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="min-h-screen flex items-center justify-center bg-[var(--xl-bg)] px-4"
    >
      <div className="w-full max-w-sm">
        <div className="bg-[var(--xl-surf)] rounded-2xl border border-[var(--xl-line)] shadow-[0_8px_30px_rgba(22,32,46,0.06)] overflow-hidden">
          <div
            className="flex flex-col items-center px-4 pt-4 pb-3 border-b border-[var(--xl-line)] bg-[var(--xl-surf)]"
            data-testid="reset-password-brand"
          >
            <XLSyncLogo size="xl" className="w-full max-w-[280px]" />
            <p className="text-sm text-[var(--xl-mut)] mt-2">
              Choose a new password
            </p>
          </div>

          <div className="p-6 space-y-5">
            {tokenError ? (
              <div
                data-testid="reset-password-token-error"
                className="space-y-4"
              >
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                  {tokenError}
                </div>
                <Link
                  to="/forgot-password"
                  data-testid="reset-password-request-new"
                  className="inline-flex w-full items-center justify-center h-11 rounded-full font-semibold text-sm bg-[var(--xl-primary)] text-white hover:opacity-90"
                >
                  Request a new reset link
                </Link>
                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-xs text-[var(--xl-mut)] hover:text-[var(--xl-text)]"
                  >
                    Back to sign in
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="new-password" className="text-xs text-[var(--xl-mut)]">
                    New password
                  </Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      data-testid="reset-password-input"
                      type={showPw ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (fieldError) setFieldError("");
                      }}
                      className="pr-10 border-[var(--xl-line2)] focus-visible:ring-primary/25"
                      required
                      minLength={MIN_PASSWORD_LENGTH}
                    />
                    <button
                      type="button"
                      data-testid="reset-password-toggle"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--xl-mut2)] hover:text-[var(--xl-text)]"
                      aria-label={showPw ? "Hide password" : "Show password"}
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password" className="text-xs text-[var(--xl-mut)]">
                    Confirm password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      data-testid="reset-password-confirm-input"
                      type={showConfirmPw ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (fieldError) setFieldError("");
                      }}
                      className="pr-10 border-[var(--xl-line2)] focus-visible:ring-primary/25"
                      required
                      minLength={MIN_PASSWORD_LENGTH}
                    />
                    <button
                      type="button"
                      data-testid="reset-password-confirm-toggle"
                      onClick={() => setShowConfirmPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--xl-mut2)] hover:text-[var(--xl-text)]"
                      aria-label={showConfirmPw ? "Hide password" : "Show password"}
                    >
                      {showConfirmPw ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {fieldError && (
                  <div
                    data-testid="reset-password-field-error"
                    className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2"
                  >
                    {fieldError}
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="reset-password-submit-btn"
                  disabled={submitting}
                  className="w-full h-11 rounded-full font-semibold"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating…
                    </>
                  ) : (
                    "Reset password"
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>

        <p className="text-xs text-[var(--xl-mut2)] text-center mt-6 space-x-2">
          <span>XLSync · Clinical imaging intelligence · Admin-invite only</span>
          <span aria-hidden>·</span>
          <Link
            to="/privacy"
            data-testid="reset-password-privacy-link"
            className="text-[var(--xl-primary)] hover:underline"
          >
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
}
